import { MILLION } from 'constants/dataSets';
import {
  ADDRESS_BUILDING,
  ADDRESS_CITY,
  ADDRESS_COORDINATES,
  ADDRESS_COUNTRY,
  ADDRESS_POST_CODE,
  ADDRESS_STATE,
  ADDRESS_STREET,
  DATE_CLOSED,
  DATE_OPENED,
  END_DATE,
  EQUITY_SHAREHOLDING,
  EU_TAXONOMY_CODE,
  FINANCIAL_CONTROL,
  FINISHED_GOOD_STOCK_AMOUNT, FINISHED_GOOD_STOCK_AMOUNT_CURRENCY,
  GROSS_MARGIN_ROLLING_AMOUNT, GROSS_MARGIN_ROLLING_CURRENCY,
  GROSS_MARGIN_ROLLING_PERCENTS,
  NACE_CODE,
  NACE_SECTION,
  NET_BOOK,
  NET_BOOK_CURRENCY,
  NUMBER_OF_EMPLOYEES,
  OLD_ORG_PATH,
  ORG_PATH,
  OWNERSHIP,
  PROFIT_CENTRE_NAME, PROFIT_CENTRE_UNIQUE_IDENTIFIER,
  REVENUE_ROLLING_AMOUNT,
  REVENUE_ROLLING_CURRENCY,
  REVENUE_ROLLING_PERCENTS,
  START_DATE,
  STRUCTURE,
  TOTAL_GROSS_PROFIT_PERCENTS,
  TOTAL_REVENUE_PERCENTS,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { DATA_SETS_DATE_FORMAT, VC_BOARD_TYPES } from 'constants/general';
import dayjs from 'dayjs';
import getNaceLabel from 'utils/getNaceLabel';
import onGetCurrentPath from 'utils/onGetCurrentOrgPath';

import getFormValue from '../getFormValue';

import type { IGetProfitCentreDefaultValues } from './interfaces/IGetProfitCentreDefaultValues';

function getProfitCentreDefaultValues({
  values, companyCurrency, naceCodeTreeData, locationsCountries, valueChainSectionCurrent,
}: IGetProfitCentreDefaultValues) {
  const {
    geoPath, name, uniqueIdentifier, valueChainSection, ownership,
    equityShareholding, financialControl, numberOfEmployees, dateOpened, dateClosed,
    naceSection, naceCode, euTaxonomyCode, startDate, endDate, addressCountry,
    addressState, addressPostCode, addressCity, addressStreet, addressBuilding, addressCoordinates,
    netBookValue, totalRevenueRolling12MActual, totalGrossMarginRolling12MActual,
    wipFinishedGoodsStock, orgPath, virtualProperties, virtualPropertyStatuses,
  } = values || {};
  const defaultOption = { value: '', label: '' };

  const { naceSectionLabel, naceDvisions, naceCodeLabel } = getNaceLabel(
    { naceCode: naceCode || '', naceSection, naceCodeTreeData },
  );

  const locationsCountriesOptions = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return {
      value: country?.iso2 || countryCode,
      label: country?.name || countryCode,
    };
  };

  return {
    [STRUCTURE]: geoPath || '',
    [PROFIT_CENTRE_NAME]: name || '',
    [PROFIT_CENTRE_UNIQUE_IDENTIFIER]: uniqueIdentifier || '',
    [VALUE_CHAIN_SECTION]: {
      value: valueChainSectionCurrent || valueChainSection?.split('. ')[1],
      label: valueChainSectionCurrent || valueChainSection?.split('. ')[1],
    },
    [VALUE_CHAIN_LIST]: {
      value: VC_BOARD_TYPES.OWN_OPERATIONS,
      label: VC_BOARD_TYPES.OWN_OPERATIONS,
    },
    [OWNERSHIP]: ownership ? {
      value: ownership, label: ownership,
    } : defaultOption,
    [EQUITY_SHAREHOLDING]: getFormValue({ value: equityShareholding }),
    [FINANCIAL_CONTROL]: getFormValue({ value: financialControl }),
    [NUMBER_OF_EMPLOYEES]: getFormValue({ value: numberOfEmployees }),
    [DATE_OPENED]: dateOpened ? dayjs(dateOpened).format(DATA_SETS_DATE_FORMAT) : '',
    [DATE_CLOSED]: dateClosed ? dayjs(dateClosed).format(DATA_SETS_DATE_FORMAT) : '',
    [NACE_SECTION]: naceSection ? {
      value: naceSection,
      label: naceSectionLabel || '',
      divisions: naceDvisions,
    } : defaultOption,
    [NACE_CODE]: naceCode ? {
      value: naceCode,
      label: naceCodeLabel || '',
    } : defaultOption,
    [EU_TAXONOMY_CODE]: euTaxonomyCode || '',
    [START_DATE]: startDate ? dayjs(startDate).format(DATA_SETS_DATE_FORMAT) : '',
    [END_DATE]: endDate ? dayjs(endDate).format(DATA_SETS_DATE_FORMAT) : '',
    [ADDRESS_COUNTRY]: addressCountry ? locationsCountriesOptions(addressCountry) : defaultOption,
    [ADDRESS_STATE]: addressState ? {
      value: addressState, label: addressState,
    } : defaultOption,
    [ADDRESS_POST_CODE]: addressPostCode || '',
    [ADDRESS_CITY]: addressCity ? {
      value: addressCity, label: addressCity,
    } : defaultOption,
    [ADDRESS_STREET]: addressStreet || '',
    [ADDRESS_BUILDING]: addressBuilding || '',
    [ADDRESS_COORDINATES]: addressCoordinates || '',
    [NET_BOOK]: netBookValue?.amount || netBookValue?.amount === 0
      ? getFormValue({ value: netBookValue.amount / MILLION }) : '',
    [NET_BOOK_CURRENCY]: netBookValue?.currency || companyCurrency || '',
    [REVENUE_ROLLING_AMOUNT]: totalRevenueRolling12MActual?.amount || totalRevenueRolling12MActual?.amount === 0
      ? getFormValue({ value: totalRevenueRolling12MActual.amount / MILLION })
      : '',
    [REVENUE_ROLLING_CURRENCY]: totalRevenueRolling12MActual?.currency || companyCurrency || '',
    [GROSS_MARGIN_ROLLING_AMOUNT]: totalGrossMarginRolling12MActual?.amount
    || totalGrossMarginRolling12MActual?.amount === 0
      ? getFormValue({ value: totalGrossMarginRolling12MActual.amount / MILLION })
      : '',
    [GROSS_MARGIN_ROLLING_CURRENCY]: totalGrossMarginRolling12MActual?.currency || companyCurrency || '',
    [FINISHED_GOOD_STOCK_AMOUNT]: wipFinishedGoodsStock?.amount || wipFinishedGoodsStock?.amount === 0
      ? getFormValue({ value: wipFinishedGoodsStock.amount / MILLION })
      : '',
    [FINISHED_GOOD_STOCK_AMOUNT_CURRENCY]: wipFinishedGoodsStock?.currency || companyCurrency || '',
    [REVENUE_ROLLING_PERCENTS]: getFormValue({ value: virtualProperties?.totalRevenuePercents }) || '',
    [GROSS_MARGIN_ROLLING_PERCENTS]: getFormValue({ value: virtualProperties?.totalGrossProfitPercents }) || '',
    [OLD_ORG_PATH]: orgPath && name ? `${onGetCurrentPath(orgPath)}/ ${name}` : '',
    [ORG_PATH]: orgPath && name ? `${onGetCurrentPath(orgPath)}/ ${name}` : '',
    [TOTAL_REVENUE_PERCENTS]: virtualPropertyStatuses?.totalRevenuePercents?.status || {},
    [TOTAL_GROSS_PROFIT_PERCENTS]: virtualPropertyStatuses?.totalGrossProfitPercents?.status || {},
  };
}

export default getProfitCentreDefaultValues;
